import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import { Grid } from './SimpleGridLight';
import { GridColumn } from './GridColumn';
import { RowCollection } from './RowCollection';
import { GridRender } from './GridRender';
import { GridCell } from './GridCell';
import { IDestroyable, safeDestroy } from '@Common/Core/Destroyable';

export class ColumnCollection<TData = any> implements IDestroyable {
    public sizingLogging = false;
    public onColumnWidthChanged = MulticastDelegate.create<ICallback<GridColumn>>();
    private readonly columns: GridColumn[];
    private readonly grid: Grid;
    private readonly render: GridRender;

    constructor(grid: Grid, render: GridRender, rows: RowCollection) {
        this.render = render;
        this.grid = grid;
        this.columns = render.renderColumns(this);

        for (const col of this.columns) {
            col.editor().buildEditorForAll(col);
            col.editor().refreshAll(rows);
        }
    }

    public destroy(): void {
        for (const col of this.columns) {
            safeDestroy(col.editor());
        }
    }

    public ToArray(): GridColumn[] {
        return this.columns;
    }

    public setReadonly(gridColumn: GridColumn) {
        const readonly = gridColumn.readonly();
        for (const row of this.grid.rows()) {
            row.getCell(gridColumn).readonly(readonly);
        }
    }

    public get(col: number): GridColumn {
        return this.columns[col];
    }

    public getByDataIndex(column: keyof TData): GridColumn {
        const filter = this.columns.filter(gc => gc.dataIndex() === column);
        if (filter.length > 0) {
            return filter[0];
        }
        throw "Invalid argument column = '{0}'".format(column);
    }

    public setValue(gridColumn: GridColumn, value: any): void {
        this.grid.beginLoad();

        for (const row of this.grid.rows()) {
            if (row.isVisible()) {
                const gridCell = row.getCell(gridColumn);
                if (!gridCell.readonly() && gridCell.value() !== value) {
                    gridCell.value(value);
                }
            }
        }
        this.grid.endLoad();
    }

    public getColumnCells(cell: GridCell): GridCell[] {
        return this.grid.rowsCollection
            .ToArray()
            .map(r => r.getCell(cell.getColumn()))
            .filter(gridCell => gridCell !== cell);
    }
}
